import React, { useState, useEffect } from 'react';
import { getSales, getSale } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    Receipt,
    MagnifyingGlass,
    Eye,
    Calendar,
    User,
    CurrencyDollar,
    Envelope,
    Phone,
    WhatsappLogo,
    ChatCircleText,
    Printer,
    Certificate,
    Tag
} from '@phosphor-icons/react';

const SalesPage = () => {
    const navigate = useNavigate();
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSale, setSelectedSale] = useState(null);
    const [showDetail, setShowDetail] = useState(false);

    useEffect(() => {
        fetchSales();
    }, []);

    const fetchSales = async () => {
        try {
            const response = await getSales();
            setSales(response.data);
        } catch (error) {
            toast.error('Error al cargar ventas');
        } finally {
            setLoading(false);
        }
    };

    const viewSaleDetail = async (saleId) => {
        try {
            const response = await getSale(saleId);
            setSelectedSale(response.data);
            setShowDetail(true);
        } catch (error) {
            toast.error('Error al cargar detalle');
        }
    };

    const handleValidatePayment = async (saleId, checked) => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/sales/${saleId}/${checked ? 'validate-payment' : 'unvalidate-payment'}`;
            const token = localStorage.getItem('token');
            const res = await fetch(url, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Error al validar');
            }
            // Optimistic update
            setSales(prev => prev.map(s => (s.id === saleId ? { ...s, payment_validated: checked } : s)));
            toast.success(checked ? 'Pago validado' : 'Validación revertida');
        } catch (e) {
            toast.error(e.message || 'No se pudo actualizar');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getDeliveryIcon = (method) => {
        switch (method) {
            case 'email': return <Envelope size={16} />;
            case 'whatsapp': return <WhatsappLogo size={16} />;
            case 'sms': return <ChatCircleText size={16} />;
            case 'print': return <Printer size={16} />;
            default: return <Envelope size={16} />;
        }
    };

    const filteredSales = sales.filter(sale => 
        sale.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.items?.some(item => item.folio?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F2D4B]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="sales-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-editorial text-2xl sm:text-3xl font-medium text-[#1F2D4B]">
                        Historial de Ventas
                    </h1>
                    <p className="text-[#4B5878] mt-1">
                        {sales.length} ventas registradas
                    </p>
                </div>
                
                {/* Search */}
                <div className="relative w-full sm:w-64">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5878]" />
                    <Input
                        placeholder="Buscar por folio o cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="sales-search-input"
                        className="h-12 pl-10 rounded-xl border-[#ECE3D2]"
                    />
                </div>
            </div>

            {/* Sales Table */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl overflow-hidden">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-[#F8F4EC] hover:bg-[#F8F4EC]">
                                    <TableHead className="font-semibold text-[#1F2D4B]">Cliente</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Vendedor</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Sucursal</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Certificados</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Total</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Pago</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Tarjeta</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">TPV</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Nº Operación</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Pago validado</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Fecha</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredSales.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={12} className="text-center py-8 text-[#4B5878]">
                                            <Receipt size={40} weight="duotone" className="mx-auto mb-2 opacity-50" />
                                            <p>No hay ventas registradas</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredSales.map((sale) => (
                                        <TableRow key={sale.id} className="hover:bg-[#F8F4EC]/50">
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium text-[#1F2D4B]">{sale.customer?.name || '-'}</p>
                                                    <p className="text-xs text-[#4B5878]">{sale.customer?.email || sale.customer?.phone}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-[#1F2D4B]">
                                                {sale.seller?.name || '—'}
                                            </TableCell>
                                            <TableCell className="text-sm text-[#4B5878]" data-testid={`sale-location-${sale.id}`}>
                                                {sale.location?.name || '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="border-[#1F2D4B] text-[#FB7185]">
                                                    {sale.total_items || 1} cert.
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="font-semibold text-[#059669]">
                                                {formatCurrency(sale.total_amount)}
                                            </TableCell>
                                            <TableCell className="text-xs uppercase tracking-wider text-[#8B8474]">
                                                {sale.is_courtesy
                                                    ? 'Cortesía'
                                                    : sale.payment_method === 'mixed'
                                                    ? 'Mixto'
                                                    : sale.payment_method === 'cash'
                                                    ? 'Efectivo'
                                                    : sale.payment_method === 'credit_card'
                                                    ? 'T. Crédito'
                                                    : sale.payment_method === 'debit_card'
                                                    ? 'T. Débito'
                                                    : sale.payment_method || '—'}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs text-[#1F2D4B]" data-testid={`card-last4-${sale.id}`}>
                                                {sale.card_last4 ? `•••• ${sale.card_last4}` : '—'}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs text-[#4B5878]" data-testid={`tpv-id-${sale.id}`}>
                                                {sale.tpv_name || sale.tpv_id || '—'}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs text-[#4B5878]" data-testid={`op-number-${sale.id}`}>
                                                {sale.operation_number || '—'}
                                            </TableCell>
                                            <TableCell>
                                                {sale.is_courtesy ? (
                                                    <span className="text-xs text-[#8B8474] italic">N/A</span>
                                                ) : sale.cash_cut_id ? (
                                                    <Badge className="bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0] hover:bg-[#ECFDF5]">
                                                        Cortado
                                                    </Badge>
                                                ) : (
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!sale.payment_validated}
                                                            onChange={(e) => handleValidatePayment(sale.id, e.target.checked)}
                                                            data-testid={`validate-payment-${sale.id}`}
                                                            className="w-4 h-4 accent-[#1F2D4B] cursor-pointer"
                                                        />
                                                        <span className={`text-xs ${sale.payment_validated ? 'text-[#065F46] font-semibold' : 'text-[#8B8474]'}`}>
                                                            {sale.payment_validated ? 'Validado' : 'Pendiente'}
                                                        </span>
                                                    </label>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-[#4B5878] text-sm">
                                                {formatDate(sale.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => viewSaleDetail(sale.id)}
                                                        data-testid={`view-sale-${sale.id}`}
                                                        className="text-[#1F2D4B] hover:text-[#FB7185] hover:bg-[#FB7185]/10 h-9 w-9 p-0"
                                                        title="Ver detalle"
                                                    >
                                                        <Eye size={18} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/tickets/${sale.id}`)}
                                                        data-testid={`reprint-sale-${sale.id}`}
                                                        className="text-[#1F2D4B] hover:text-[#FB7185] hover:bg-[#FB7185]/10 h-9 w-9 p-0"
                                                        title="Reimprimir ticket"
                                                    >
                                                        <Printer size={18} />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Sale Detail Modal */}
            <Dialog open={showDetail} onOpenChange={setShowDetail}>
                <DialogContent className="sm:max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-editorial text-xl text-[#1F2D4B]">
                            Detalle de Venta
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedSale && (
                        <div className="space-y-4">
                            {/* Customer */}
                            <div className="bg-[#F8F4EC] rounded-xl p-4">
                                <h4 className="font-medium text-[#1F2D4B] mb-2 flex items-center gap-2">
                                    <User size={18} weight="duotone" className="text-[#FB7185]" />
                                    Cliente
                                </h4>
                                <p className="font-medium">{selectedSale.customer?.name}</p>
                                <p className="text-sm text-[#4B5878]">{selectedSale.customer?.email}</p>
                                <p className="text-sm text-[#4B5878]">{selectedSale.customer?.phone}</p>
                            </div>

                            {/* Items/Certificates */}
                            <div>
                                <h4 className="font-medium text-[#1F2D4B] mb-3 flex items-center gap-2">
                                    <Certificate size={18} weight="duotone" className="text-[#FB7185]" />
                                    Certificados ({selectedSale.items?.length || 0})
                                </h4>
                                <div className="space-y-2">
                                    {selectedSale.items?.map((item, index) => (
                                        <div 
                                            key={index}
                                            className="bg-white border border-[#ECE3D2] rounded-xl p-3"
                                        >
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <Badge className="bg-[#1F2D4B] text-white border-0 font-mono mb-1">
                                                        {item.folio}
                                                    </Badge>
                                                    <p className="font-medium text-[#1F2D4B]">{item.product_name}</p>
                                                    <p className="text-xs text-[#4B5878]">
                                                        Válido hasta: {new Date(item.valid_until).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City' })}
                                                    </p>
                                                </div>
                                                <span className="font-semibold text-[#059669]">
                                                    {formatCurrency(item.unit_price)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Total */}
                            <div className="bg-[#F8F4EC] border border-[#ECE3D2] rounded-xl p-4 flex justify-between items-center">
                                <span className="text-lg font-medium text-[#1F2D4B]">Total</span>
                                <span className="font-editorial text-2xl font-bold text-[#FB7185]">
                                    {formatCurrency(selectedSale.total_amount)}
                                </span>
                            </div>

                            {/* Meta */}
                            <div className="flex justify-between text-xs text-[#4B5878] pt-2">
                                <span>Vendedor: {selectedSale.seller?.name}</span>
                                <span>{formatDate(selectedSale.created_at)}</span>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 pt-3 border-t border-[#ECE3D2]">
                                <Button
                                    onClick={() => navigate(`/tickets/${selectedSale.id}`)}
                                    data-testid="modal-print-ticket-btn"
                                    className="flex-1 h-11 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                                >
                                    <Printer size={16} weight="bold" className="mr-1.5" /> Imprimir ticket
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SalesPage;
